import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import type { Event } from "@lezz/shared";

dayjs.extend(utc);

/**
 * Events only store a start time (`dateAndTime`), so calendar entries default to a
 * fixed duration.
 */
const DEFAULT_EVENT_DURATION_HOURS = 2;

/** File name used when sharing/downloading the generated `.ics` file. */
export const ICS_FILE_NAME = "evento.ics";

/**
 * Returns the event start/end as absolute UTC instants.
 * `dateAndTime` is stored in UTC ISO format, so `dayjs.utc` gives the correct instant.
 */
export function getEventTimes(event: Event): {
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
} {
  const start = dayjs.utc(event.dateAndTime);
  const end = start.add(DEFAULT_EVENT_DURATION_HOURS, "hour");
  return { start, end };
}

/** Human-readable location string (mirrors the copy-address behaviour in event detail). */
export function buildEventLocation(event: Event): string {
  return `${event.address}, ${event.city}, ${event.state} - ${event.country}`;
}

/** Formats a UTC instant as the compact form calendars expect: `YYYYMMDDTHHmmssZ`. */
function formatUtcStamp(date: dayjs.Dayjs): string {
  return date.format("YYYYMMDDTHHmmss[Z]");
}

/**
 * Builds a Google Calendar "add event" template URL. Opens in the browser or the
 * Google Calendar app with the event pre-filled.
 */
export function buildGoogleCalendarUrl(event: Event): string {
  const { start, end } = getEventTimes(event);
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: event.title,
    dates: `${formatUtcStamp(start)}/${formatUtcStamp(end)}`,
    details: event.description ?? "",
    location: buildEventLocation(event),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Escapes a text value per RFC 5545 (backslash, comma, semicolon, newlines). */
function escapeIcsText(value: string): string {
  return value
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\r?\n/g, "\\n");
}

/**
 * Builds an RFC 5545 iCalendar (`.ics`) document for the event. Compatible with
 * Apple Calendar, Outlook and other calendar clients.
 */
export function buildIcsContent(event: Event): string {
  const { start, end } = getEventTimes(event);
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Lezz//Event//PT",
    "CALSCALE:GREGORIAN",
    "BEGIN:VEVENT",
    `UID:${event.eventId}@lezz.social`,
    `DTSTAMP:${formatUtcStamp(dayjs.utc())}`,
    `DTSTART:${formatUtcStamp(start)}`,
    `DTEND:${formatUtcStamp(end)}`,
    `SUMMARY:${escapeIcsText(event.title)}`,
    `DESCRIPTION:${escapeIcsText(event.description ?? "")}`,
    `LOCATION:${escapeIcsText(buildEventLocation(event))}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}
